import { useState, useMemo } from "react";
import {
  X,
  Save,
  Plus,
  Edit2,
  Trash2,
  Euro,
  AlertCircle,
  Info,
  Filter,
  Check,
  Clock,
  Calendar,
  Slash,
  Zap,
  CircleOff,
  Scan,
  Camera
} from "lucide-react";
import Button from "./ui/Button";
import Input from "./ui/Input";
import Select from "./ui/Select";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "./ui/Dialog";

interface Damage {
  _id?: string;
  type: "Rayure" | "Choc" | "Manquant" | "Casse";
  face: "avant" | "arriere" | "coteConducteur" | "cotePassager" | "toit";
  position: { x: number; y: number };
  description: string;
  cost?: number;
  images?: string[];
  date?: Date | string;
  isRepaired?: boolean;
  repairedAt?: Date | string;
  driverName?: string;
  driverId?: string;
}

interface VehicleDamageReporterProps {
  damages: Damage[];
  onAddDamage: (damage: Damage) => void;
  onUpdateDamage: (damageId: string, updates: Partial<Damage>) => void;
  onDeleteDamage: (damageId: string) => void;
  readOnly?: boolean;
}

const DAMAGE_COLORS = {
  Rayure: "#FFA500", // Orange
  Choc: "#FF0000",   // Rouge
  Manquant: "#0000FF", // Bleu
  Casse: "#800080"   // Violet
};

const FACES = {
  avant: "Avant",
  arriere: "Arrière",
  coteConducteur: "Côté Conducteur",
  cotePassager: "Côté Passager",
  toit: "Toit"
};

const FACE_IMAGES = {
  avant: "face.png",
  arriere: "derriere.png",
  coteConducteur: "gauche.png",
  cotePassager: "droite.png",
  toit: "toit.png"
};

// Fonction pour calculer la couleur selon l'ancienneté
const getAgeOpacity = (date?: Date | string): number => {
  if (!date) return 1;
  
  const damageDate = new Date(date);
  const now = new Date();
  const daysDiff = Math.floor((now.getTime() - damageDate.getTime()) / (1000 * 60 * 60 * 24));
  
  if (daysDiff <= 7) return 1; // Moins d'une semaine : opacité 100%
  if (daysDiff <= 30) return 0.8; // Moins d'un mois : opacité 80%
  if (daysDiff <= 90) return 0.6; // Moins de 3 mois : opacité 60%
  if (daysDiff <= 180) return 0.4; // Moins de 6 mois : opacité 40%
  return 0.3; // Plus de 6 mois : opacité 30%
};

// Fonction pour obtenir le label d'ancienneté
const getAgeLabel = (date?: Date | string): string => {
  if (!date) return "Date inconnue";
  
  const damageDate = new Date(date);
  const now = new Date();
  const daysDiff = Math.floor((now.getTime() - damageDate.getTime()) / (1000 * 60 * 60 * 24));
  
  if (daysDiff === 0) return "Aujourd'hui";
  if (daysDiff === 1) return "Hier";
  if (daysDiff <= 7) return `Il y a ${daysDiff} jours`;
  if (daysDiff <= 30) return `Il y a ${Math.floor(daysDiff / 7)} semaine${Math.floor(daysDiff / 7) > 1 ? 's' : ''}`;
  if (daysDiff <= 90) return `Il y a ${Math.floor(daysDiff / 30)} mois`;
  if (daysDiff <= 365) return `Il y a ${Math.floor(daysDiff / 30)} mois`;
  return `Il y a ${Math.floor(daysDiff / 365)} an${Math.floor(daysDiff / 365) > 1 ? 's' : ''}`;
};

// Fonction pour obtenir l'icône selon le type de dégât
const getDamageIcon = (type: string) => {
  switch (type) {
    case "Rayure":
      return <Scan className="w-3 h-3 text-white" />;
    case "Choc":
      return <Zap className="w-3 h-3 text-white" />;
    case "Manquant":
      return <CircleOff className="w-3 h-3 text-white" />;
    case "Casse":
      return <Slash className="w-3 h-3 text-white" />;
    default:
      return null;
  }
};

const VehicleDamageReporter = ({
  damages,
  onAddDamage,
  onUpdateDamage,
  onDeleteDamage,
  readOnly = false
}: VehicleDamageReporterProps) => {
  const [selectedFace, setSelectedFace] = useState<keyof typeof FACES>("avant");
  const [showDamageDialog, setShowDamageDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [viewingDamage, setViewingDamage] = useState<Damage | null>(null);
  const [editingDamage, setEditingDamage] = useState<Damage | null>(null);
  const [damageForm, setDamageForm] = useState<Partial<Damage>>({
    type: "Rayure",
    description: "",
    cost: 0,
    images: []
  });
  const [clickPosition, setClickPosition] = useState<{ x: number; y: number } | null>(null);
  
  // États pour les filtres
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    type: "all" as "all" | Damage["type"],
    repairStatus: "all" as "all" | "repaired" | "notRepaired",
    dateRange: "all" as "all" | "week" | "month" | "quarter" | "semester" | "year"
  });

  const handleVehicleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (readOnly) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    setClickPosition({ x, y });
    setDamageForm({
      type: "Rayure",
      description: "",
      cost: 0,
      images: [],
      face: selectedFace,
      position: { x, y }
    });
    setEditingDamage(null);
    setShowDamageDialog(true);
  };

  const handleEditDamage = (damage: Damage) => {
    setEditingDamage(damage);
    setDamageForm({
      type: damage.type,
      description: damage.description,
      cost: damage.cost || 0,
      face: damage.face,
      position: damage.position
    });
    setShowDamageDialog(true);
  };

  const handleViewDamage = (damage: Damage) => {
    setViewingDamage(damage);
    setShowDetailDialog(true);
  };

  const handleEditFromDetail = () => {
    if (viewingDamage) {
      setShowDetailDialog(false);
      handleEditDamage(viewingDamage);
    }
  };

  const handleToggleRepairFromDetail = () => {
    if (viewingDamage && viewingDamage._id) {
      onUpdateDamage(viewingDamage._id, { isRepaired: !viewingDamage.isRepaired });
      // Mettre à jour l'état local
      setViewingDamage({ ...viewingDamage, isRepaired: !viewingDamage.isRepaired });
    }
  };

  const handleSaveDamage = () => {
    if (editingDamage && editingDamage._id) {
      // Mise à jour d'un dégât existant
      onUpdateDamage(editingDamage._id, {
        type: damageForm.type,
        description: damageForm.description,
        cost: damageForm.cost
      });
    } else {
      // Nouveau dégât
      onAddDamage({
        type: damageForm.type as "Rayure" | "Choc" | "Manquant" | "Casse",
        face: damageForm.face as "avant" | "arriere" | "coteConducteur" | "cotePassager" | "toit",
        position: damageForm.position!,
        description: damageForm.description || "",
        cost: damageForm.cost || 0,
        images: damageForm.images || []
      });
    }
    
    setShowDamageDialog(false);
    setEditingDamage(null);
    setDamageForm({
      type: "Rayure",
      description: "",
      cost: 0,
      images: []
    });
  };

  // Filtrage des dégâts
  const filteredDamages = useMemo(() => {
    let filtered = [...damages];
    
    // Filtre par type
    if (filters.type !== "all") {
      filtered = filtered.filter(d => d.type === filters.type);
    }
    
    // Filtre par statut de réparation
    if (filters.repairStatus === "repaired") {
      filtered = filtered.filter(d => d.isRepaired === true);
    } else if (filters.repairStatus === "notRepaired") {
      filtered = filtered.filter(d => !d.isRepaired);
    }
    
    // Filtre par date
    if (filters.dateRange !== "all") {
      const now = new Date();
      const filterDate = new Date();
      
      switch (filters.dateRange) {
        case "week":
          filterDate.setDate(now.getDate() - 7);
          break;
        case "month":
          filterDate.setMonth(now.getMonth() - 1);
          break;
        case "quarter":
          filterDate.setMonth(now.getMonth() - 3);
          break;
        case "semester":
          filterDate.setMonth(now.getMonth() - 6);
          break;
        case "year":
          filterDate.setFullYear(now.getFullYear() - 1);
          break;
      }
      
      filtered = filtered.filter(d => {
        if (!d.date) return true; // Garder les dégâts sans date
        const damageDate = new Date(d.date);
        return damageDate >= filterDate;
      });
    }
    
    return filtered;
  }, [damages, filters]);

  const currentFaceDamages = filteredDamages.filter(d => d.face === selectedFace);
  const totalCost = filteredDamages.reduce((sum, d) => sum + (d.cost || 0), 0);

  return (
    <div className="space-y-6">
      {/* En-tête avec statistiques */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-blue-600" />
              <span className="font-semibold text-gray-900">
                {damages.length} dégât{damages.length > 1 ? 's' : ''} signalé{damages.length > 1 ? 's' : ''}
              </span>
            </div>
            <div className="h-6 w-px bg-blue-300"></div>
            <div className="flex items-center gap-2">
              <Euro className="w-5 h-5 text-green-600" />
              <span className="font-semibold text-gray-900">
                Coût total : {totalCost.toFixed(2)} €
              </span>
            </div>
          </div>
          {!readOnly && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Info className="w-4 h-4" />
              Cliquez sur le véhicule pour ajouter un dégât
            </div>
          )}
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 font-medium text-gray-900 hover:text-blue-600 transition-colors"
          >
            <Filter className="w-5 h-5" />
            Filtres
            {(filters.type !== "all" || filters.repairStatus !== "all" || filters.dateRange !== "all") && (
              <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-semibold">
                Actifs
              </span>
            )}
          </button>
          
          {(filters.type !== "all" || filters.repairStatus !== "all" || filters.dateRange !== "all") && (
            <button
              onClick={() => setFilters({ type: "all", repairStatus: "all", dateRange: "all" })}
              className="text-sm text-gray-600 hover:text-red-600 transition-colors"
            >
              Réinitialiser
            </button>
          )}
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-3 border-t border-gray-200">
            {/* Filtre par type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type de dégât
              </label>
              <select
                value={filters.type}
                onChange={(e) => setFilters({ ...filters, type: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">Tous les types</option>
                <option value="Rayure">Rayure</option>
                <option value="Choc">Choc</option>
                <option value="Manquant">Manquant</option>
                <option value="Casse">Casse</option>
              </select>
            </div>

            {/* Filtre par statut de réparation */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Statut de réparation
              </label>
              <select
                value={filters.repairStatus}
                onChange={(e) => setFilters({ ...filters, repairStatus: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">Tous les statuts</option>
                <option value="notRepaired">Non réparés</option>
                <option value="repaired">Réparés</option>
              </select>
            </div>

            {/* Filtre par période */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Période
              </label>
              <select
                value={filters.dateRange}
                onChange={(e) => setFilters({ ...filters, dateRange: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">Toutes les périodes</option>
                <option value="week">Cette semaine</option>
                <option value="month">Ce mois</option>
                <option value="quarter">3 derniers mois</option>
                <option value="semester">6 derniers mois</option>
                <option value="year">Cette année</option>
              </select>
            </div>
          </div>
        )}
        
        {/* Résultats du filtrage */}
        {(filters.type !== "all" || filters.repairStatus !== "all" || filters.dateRange !== "all") && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              <span className="font-semibold text-gray-900">{filteredDamages.length}</span> dégât{filteredDamages.length > 1 ? 's' : ''} {filteredDamages.length > 1 ? 'correspondent' : 'correspond'} aux filtres
              {damages.length !== filteredDamages.length && (
                <span className="text-gray-500"> (sur {damages.length} total)</span>
              )}
            </p>
          </div>
        )}
      </div>

      {/* Sélecteur de face */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium text-gray-700">Vue :</span>
        {Object.entries(FACES).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setSelectedFace(key as keyof typeof FACES)}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              selectedFace === key
                ? "bg-blue-600 text-white shadow-md"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Croquis du véhicule */}
      <div className="bg-white rounded-xl border-2 border-gray-200 p-4 shadow-sm">
        <div
          className="relative w-full bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg cursor-crosshair overflow-hidden border border-gray-300"
          onClick={handleVehicleClick}
          style={{
            backgroundImage: `url(/images/vehicles/${FACE_IMAGES[selectedFace]})`,
            backgroundSize: "contain",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            minHeight: "350px",
            maxHeight: "500px",
            aspectRatio: "16/10"
          }}
        >
          {/* Affichage des dégâts existants */}
          {currentFaceDamages.map((damage, index) => (
            <div
              key={damage._id || index}
              className="absolute group z-20"
              style={{
                left: `${damage.position?.x || 50}%`,
                top: `${damage.position?.y || 50}%`,
                transform: 'translate(-50%, -50%)'
              }}
            >
              {/* Point de dégât */}
              <div className="relative">
                <div
                  className="w-10 h-10 rounded-full border-3 border-white shadow-2xl cursor-pointer hover:scale-125 transition-all duration-200 flex flex-col items-center justify-center gap-0.5"
                  style={{ 
                    backgroundColor: DAMAGE_COLORS[damage.type],
                    opacity: getAgeOpacity(damage.date),
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3), 0 0 0 3px white'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewDamage(damage);
                  }}
                >
                  {getDamageIcon(damage.type)}
                  <span className="text-white text-[10px] font-bold drop-shadow-lg">
                    #{index + 1}
                  </span>
                </div>
                
                {/* Badge de réparation */}
                {damage.isRepaired && (
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white flex items-center justify-center shadow-lg">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}
              </div>

              {/* Tooltip au survol */}
              <div className="absolute left-full ml-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-30 whitespace-nowrap">
                <div className="bg-gray-900 text-white text-sm rounded-lg px-4 py-3 shadow-2xl min-w-[200px]">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="font-bold text-base">{damage.type}</div>
                    {damage.isRepaired && (
                      <span className="px-2 py-0.5 bg-green-500 text-white text-xs rounded-full flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        Réparé
                      </span>
                    )}
                  </div>
                  
                  {damage.driverName && (
                    <div className="text-blue-400 text-xs mb-2 font-semibold flex items-center gap-1">
                      👤 {damage.driverName}
                    </div>
                  )}
                  
                  {damage.date && (
                    <div className="text-gray-400 text-xs mb-1 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {getAgeLabel(damage.date)}
                    </div>
                  )}
                  
                  {damage.description && (
                    <div className="text-gray-300 text-xs mb-1">{damage.description}</div>
                  )}
                  
                  {damage.cost !== undefined && damage.cost > 0 && (
                    <div className="text-yellow-400 font-bold text-sm mt-2 flex items-center gap-1">
                      <span>💰</span> {damage.cost.toFixed(2)} €
                    </div>
                  )}
                  
                  {damage.isRepaired && damage.repairedAt && (
                    <div className="text-green-400 text-xs mt-2 flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      Réparé le {new Date(damage.repairedAt).toLocaleDateString('fr-FR')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Message si pas de dégâts sur cette face */}
          {currentFaceDamages.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center text-gray-400 pointer-events-none">
              <div className="text-center bg-white/80 backdrop-blur-sm rounded-xl p-6 border-2 border-dashed border-gray-300">
                <AlertCircle className="w-16 h-16 mx-auto mb-3 opacity-50" />
                <p className="text-base font-medium text-gray-600">
                  Aucun dégât sur cette face
                </p>
                {!readOnly && (
                  <p className="text-sm text-gray-500 mt-2">
                    Cliquez sur le véhicule pour signaler un dégât
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Légende des types de dégâts */}
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Légende :</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(DAMAGE_COLORS).map(([type, color]) => (
            <div key={type} className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded-full border-2 border-white shadow"
                style={{ backgroundColor: color }}
              ></div>
              <span className="text-sm text-gray-700">{type}</span>
            </div>
          ))}
        </div>
        
        {/* Légende de l'ancienneté */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Ancienneté des dégâts (opacité) :</h4>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-red-500" style={{ opacity: 1 }}></div>
              <span className="text-gray-600">≤ 7 jours</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-red-500" style={{ opacity: 0.8 }}></div>
              <span className="text-gray-600">≤ 1 mois</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-red-500" style={{ opacity: 0.6 }}></div>
              <span className="text-gray-600">≤ 3 mois</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-red-500" style={{ opacity: 0.4 }}></div>
              <span className="text-gray-600">≤ 6 mois</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-red-500" style={{ opacity: 0.3 }}></div>
              <span className="text-gray-600">&gt; 6 mois</span>
            </div>
          </div>
        </div>
      </div>

      {/* Liste des dégâts */}
      {filteredDamages.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-900">
            Détails des dégâts ({filteredDamages.length})
          </h3>
          {filteredDamages.map((damage, index) => (
            <div
              key={damage._id || index}
              className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-200 text-gray-700 text-xs font-bold">
                      {index + 1}
                    </span>
                    <div
                      className="w-4 h-4 rounded-full border-2 border-white shadow"
                      style={{ 
                        backgroundColor: DAMAGE_COLORS[damage.type],
                        opacity: getAgeOpacity(damage.date)
                      }}
                    ></div>
                    <span className="font-semibold text-gray-900">
                      {damage.type}
                    </span>
                    <span className="text-sm text-gray-500">
                      - {FACES[damage.face]}
                    </span>
                    {damage.isRepaired && (
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1 font-semibold">
                        <Check className="w-3 h-3" />
                        Réparé
                      </span>
                    )}
                  </div>
                  
                  {damage.date && (
                    <div className="flex items-center gap-1 ml-9 mb-2 text-xs text-gray-500">
                      <Calendar className="w-3 h-3" />
                      <span>{getAgeLabel(damage.date)}</span>
                    </div>
                  )}
                  
                  {damage.driverName && (
                    <div className="flex items-center gap-1 ml-9 mb-2 text-sm">
                      <span className="text-gray-600">👤 Causé par :</span>
                      <span className="font-semibold text-blue-600">{damage.driverName}</span>
                    </div>
                  )}
                  
                  {damage.description && (
                    <p className="text-sm text-gray-600 ml-9 mb-2">
                      {damage.description}
                    </p>
                  )}
                  {damage.cost && damage.cost > 0 && (
                    <div className="flex items-center gap-2 ml-9">
                      <Euro className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-semibold text-green-700">
                        {damage.cost.toFixed(2)} €
                      </span>
                    </div>
                  )}
                  
                  {/* Affichage des images du dégât */}
                  {damage.images && damage.images.length > 0 && (
                    <div className="ml-9 mt-3">
                      <div className="flex flex-wrap gap-2">
                        {damage.images.map((img, imgIndex) => (
                          <img
                            key={imgIndex}
                            src={img}
                            alt={`Dégât ${index + 1} - Photo ${imgIndex + 1}`}
                            className="w-20 h-20 object-cover rounded-lg border-2 border-gray-200 hover:scale-150 transition-transform cursor-pointer shadow-sm"
                            onClick={() => window.open(img, '_blank')}
                            title="Cliquez pour agrandir"
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {!readOnly && (
                  <div className="flex items-center gap-2">
                    {damage._id && (
                      <button
                        onClick={() => onUpdateDamage(damage._id!, { isRepaired: !damage.isRepaired })}
                        className={`p-2 rounded-lg transition-colors ${
                          damage.isRepaired 
                            ? "text-orange-600 hover:bg-orange-50" 
                            : "text-green-600 hover:bg-green-50"
                        }`}
                        title={damage.isRepaired ? "Marquer comme non réparé" : "Marquer comme réparé"}
                      >
                        {damage.isRepaired ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                      </button>
                    )}
                    <button
                      onClick={() => handleEditDamage(damage)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Modifier"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => damage._id && onDeleteDamage(damage._id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dialog détaillé pour visualiser un dégât */}
      <Dialog open={showDetailDialog} onClose={() => setShowDetailDialog(false)} maxWidth="md">
        <DialogTitle onClose={() => setShowDetailDialog(false)}>
          Détails du dégât
        </DialogTitle>
        {viewingDamage && (
          <>
            <DialogContent>
              <div className="space-y-6">
                {/* En-tête avec type et statut */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div
                      className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg border-4 border-white"
                      style={{
                        backgroundColor: DAMAGE_COLORS[viewingDamage.type],
                        opacity: getAgeOpacity(viewingDamage.date)
                      }}
                    >
                      <div className="text-white scale-150">
                        {getDamageIcon(viewingDamage.type)}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">{viewingDamage.type}</h3>
                      <p className="text-sm text-gray-500">{FACES[viewingDamage.face]}</p>
                    </div>
                  </div>
                  {viewingDamage.isRepaired ? (
                    <div className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-full font-semibold border-2 border-green-200">
                      <Check className="w-5 h-5" />
                      Réparé
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 px-4 py-2 bg-orange-100 text-orange-700 rounded-full font-semibold border-2 border-orange-200">
                      <AlertCircle className="w-5 h-5" />
                      Non réparé
                    </div>
                  )}
                </div>

                {/* Informations principales */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Date */}
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                      <Calendar className="w-4 h-4" />
                      Date du dégât
                    </div>
                    <p className="text-lg font-semibold text-gray-900">
                      {viewingDamage.date 
                        ? new Date(viewingDamage.date).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })
                        : "Non spécifiée"}
                    </p>
                    {viewingDamage.date && (
                      <p className="text-sm text-gray-500 mt-1">
                        {getAgeLabel(viewingDamage.date)}
                      </p>
                    )}
                  </div>

                  {/* Coût */}
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                      <Euro className="w-4 h-4" />
                      Coût
                    </div>
                    <p className="text-lg font-semibold text-gray-900">
                      {viewingDamage.cost ? `${viewingDamage.cost.toFixed(2)} €` : "Non spécifié"}
                    </p>
                  </div>
                </div>

                {/* Chauffeur responsable */}
                {viewingDamage.driverName && (
                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                    <div className="flex items-center gap-2 text-blue-700 text-sm mb-1 font-medium">
                      <Info className="w-4 h-4" />
                      Chauffeur responsable
                    </div>
                    <p className="text-lg font-semibold text-blue-900">
                      {viewingDamage.driverName}
                    </p>
                  </div>
                )}

                {/* Date de réparation */}
                {viewingDamage.isRepaired && viewingDamage.repairedAt && (
                  <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                    <div className="flex items-center gap-2 text-green-700 text-sm mb-1 font-medium">
                      <Check className="w-4 h-4" />
                      Date de réparation
                    </div>
                    <p className="text-lg font-semibold text-green-900">
                      {new Date(viewingDamage.repairedAt).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                )}

                {/* Description */}
                {viewingDamage.description && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <Info className="w-4 h-4" />
                      Description
                    </h4>
                    <p className="text-gray-700 bg-gray-50 rounded-lg p-4 border border-gray-200 leading-relaxed">
                      {viewingDamage.description}
                    </p>
                  </div>
                )}

                {/* Position */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Position sur le véhicule</h4>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Face : <span className="font-semibold text-gray-900">{FACES[viewingDamage.face]}</span></span>
                      <span className="text-gray-600">
                        Coordonnées : <span className="font-semibold text-gray-900">
                          ({viewingDamage.position?.x.toFixed(1)}%, {viewingDamage.position?.y.toFixed(1)}%)
                        </span>
                      </span>
                    </div>
                  </div>
                </div>

                {/* Images */}
                {viewingDamage.images && viewingDamage.images.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Camera className="w-4 h-4" />
                      Photos ({viewingDamage.images.length})
                    </h4>
                    <div className="grid grid-cols-3 gap-3">
                      {viewingDamage.images.map((img, idx) => (
                        <div
                          key={idx}
                          className="aspect-square rounded-lg overflow-hidden border-2 border-gray-200 hover:border-blue-400 transition-colors cursor-pointer shadow-sm hover:shadow-md"
                        >
                          <img
                            src={img}
                            alt={`Dégât ${idx + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </DialogContent>
            <DialogActions>
              {!readOnly && (
                <>
                  <Button
                    variant="outlined"
                    onClick={handleToggleRepairFromDetail}
                    startIcon={viewingDamage.isRepaired ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                    className={viewingDamage.isRepaired ? "text-orange-600 border-orange-600 hover:bg-orange-50" : "text-green-600 border-green-600 hover:bg-green-50"}
                  >
                    {viewingDamage.isRepaired ? "Marquer non réparé" : "Marquer réparé"}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleEditFromDetail}
                    startIcon={<Edit2 className="w-4 h-4" />}
                    className="text-blue-600 border-blue-600 hover:bg-blue-50"
                  >
                    Modifier
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      if (viewingDamage._id) {
                        onDeleteDamage(viewingDamage._id);
                        setShowDetailDialog(false);
                      }
                    }}
                    startIcon={<Trash2 className="w-4 h-4" />}
                    className="text-red-600 border-red-600 hover:bg-red-50"
                  >
                    Supprimer
                  </Button>
                </>
              )}
              <Button
                variant="contained"
                onClick={() => setShowDetailDialog(false)}
              >
                Fermer
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Dialog pour ajouter/modifier un dégât */}
      <Dialog open={showDamageDialog} onClose={() => setShowDamageDialog(false)}>
        <DialogTitle>
          {editingDamage ? "Modifier le dégât" : "Signaler un nouveau dégât"}
        </DialogTitle>
        <DialogContent>
          <div className="space-y-4">
            <Select
              label="Type de dégât"
              value={damageForm.type}
              onChange={(e) => setDamageForm({ ...damageForm, type: e.target.value as any })}
              required
            >
              <option value="Rayure">Rayure</option>
              <option value="Choc">Choc</option>
              <option value="Manquant">Manquant</option>
              <option value="Casse">Casse</option>
            </Select>

            <Input
              label="Description"
              value={damageForm.description}
              onChange={(e) => setDamageForm({ ...damageForm, description: e.target.value })}
              placeholder="Décrivez le dégât..."
              multiline
              rows={3}
            />

            <div className="relative">
              <Input
                label="Coût estimé (€)"
                type="number"
                value={damageForm.cost || 0}
                onChange={(e) => setDamageForm({ ...damageForm, cost: parseFloat(e.target.value) || 0 })}
                min={0}
                step={0.01}
                placeholder="0.00"
              />
              <Euro className="absolute right-3 top-9 w-5 h-5 text-gray-400" />
            </div>

            {editingDamage && (
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                <div className="flex items-center gap-2 text-sm text-blue-800">
                  <Info className="w-4 h-4" />
                  <span>
                    Position : {FACES[damageForm.face!]} ({damageForm.position?.x.toFixed(1)}%, {damageForm.position?.y.toFixed(1)}%)
                  </span>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setShowDamageDialog(false)}
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveDamage}
            startIcon={<Save className="w-4 h-4" />}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default VehicleDamageReporter;

